import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { manageV2CashTransaction, manageCashV1, createV2Transaction } from '../../composables/fetchAPI'; // ✅ รอเชื่อม API จริง
import { usePortfolio } from '../../context/PortfolioContext';
import { sanitizeDecimalInput, validateCryptoDecimal, subtractDecimal, num } from '../../utils/cryptoDecimal';

interface ManageCashModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  portfolioId: string;
  currentCash: number;
  initialDate?: string;    // ✅ รับค่าวันที่เริ่มต้น
  initialAmount?: number;  // ✅ รับค่ายอดเงินเริ่มต้น
  // พอร์ตคริปโต: จำนวนเงินส่งเป็น decimal string ใน decimalValues (ห้ามแปลงเป็น Number)
  isCrypto?: boolean;
  currentCashExact?: string; // ยอดเงินสดตรงตัวจากหลังบ้าน ใช้คิดส่วนต่างตอนปรับยอด
}

const ManageCashModal: React.FC<ManageCashModalProps> = ({ isOpen, onClose, onSave, portfolioId, currentCash ,initialDate,initialAmount, isCrypto = false, currentCashExact}) => {
  // โหมดปกติ
  const [actionType, setActionType] = useState<'CASH_DEPOSIT' | 'CASH_WITHDRAW'>('CASH_DEPOSIT');
  const [amount, setAmount] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const { displayMode } = usePortfolio(); // ✅ ดึง displayMode จาก context
  const [displayAmount, setDisplayAmount] = useState(''); // ✅ ประกาศตัวนี้ไว้ด้านบนๆ

  // โหมดปรับสมดุล (Adjust Mode)
  const [isAdjustMode, setIsAdjustMode] = useState(false);
  const [targetBalance, setTargetBalance] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  // ค่าที่พิมพ์จริงของพอร์ตคริปโต (state number ด้านบนใช้แค่คำนวณยอดพรีวิวบนจอ)
  const [amountText, setAmountText] = useState('');
  const [targetText, setTargetText] = useState('');

  
// ✅ ปรับปรุง useEffect ให้เซ็ตค่าทั้ง amount และ displayAmount พร้อมกัน
  useEffect(() => {
    if (isOpen) {
      if (initialDate) {
        setTransactionDate(initialDate);
      }
      if (initialAmount !== undefined && initialAmount !== null) {
        setAmount(initialAmount);
        setDisplayAmount(initialAmount.toString());
        setAmountText(initialAmount ? String(initialAmount) : '');
      }
      setActionType('CASH_DEPOSIT');
    }
  }, [isOpen, initialDate, initialAmount]);

  // คำนวณส่วนต่างแบบ Real-time สำหรับหน้า UI
  const difference = Number(targetBalance) - currentCash;
// ✅ คำนวณยอดเงินสดล่วงหน้า (Projected Cash) แบบ Real-time ตามโหมดที่ผู้ใช้กำลังกรอก
  const projectedCash = (() => {
    if (displayMode === 'V1') {
      return typeof amount === 'number' ? amount : currentCash;
    }
    if (isAdjustMode) {
      return targetBalance !== '' ? Number(targetBalance) : currentCash;
    } else {
      const numAmount = Number(amount) || 0;
      if (actionType === 'CASH_DEPOSIT') {
        return currentCash + numAmount;
      } else {
        return currentCash - numAmount;
      }
    }
  })();

  // เช็คว่ามีการเปลี่ยนแปลงจากยอดเดิมหรือไม่ (ใช้ทศนิยมเผื่อความแม่นยำ)
  const isModified = Math.abs(projectedCash - currentCash) > 0.0001;

  // ฟังก์ชันสำหรับใส่คอมมา
  const formatNumber = (val: string) => {
    const num = val.replace(/,/g, ''); // ลบคอมมาออกก่อน
    if (isNaN(num as any) || num === '') return '';
    return Number(num).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };
  useEffect(() => {
    if (isOpen && displayMode === 'V1') {
      setAmount(currentCash); // ✅ กำหนดให้ค่าเริ่มต้นคือเงินสดที่มีอยู่จริง
    }
  }, [isOpen, currentCash, displayMode]);




  // พอร์ตคริปโต: ส่งจำนวนเงินตรงตัวใน decimalValues ส่วนการปรับยอดคิดส่วนต่างแบบแม่นยำจาก string
  const handleCryptoSubmit = async () => {
    let type: 'CASH_DEPOSIT' | 'CASH_WITHDRAW' | 'CASH_ADJUST';
    let amountValue: string;
    let noteValue = note;

    if (isAdjustMode) {
      const err = validateCryptoDecimal(targetText, { allowZero: true });
      if (err) return toast.error(`ยอดเงินเป้าหมาย: ${err}`);
      amountValue = subtractDecimal(targetText, currentCashExact ?? String(currentCash));
      if (amountValue === '0') return toast.error('ยอดเงินเท่าเดิม ไม่มีการเปลี่ยนแปลง');
      type = 'CASH_ADJUST';
      noteValue = 'ปรับข้อมูล';
    } else {
      const err = validateCryptoDecimal(amountText);
      if (err) return toast.error(`จำนวนเงิน: ${err}`);
      if (actionType === 'CASH_WITHDRAW' && num(amountText) > currentCash) return toast.error('ยอดเงินสดไม่เพียงพอสำหรับการถอน');
      type = actionType;
      amountValue = amountText.trim();
    }

    setIsLoading(true);
    try {
      await createV2Transaction(portfolioId, {
        type,
        cashCurrency: 'USD',
        note: noteValue,
        transactionDate,
        decimalValues: { amount: amountValue },
      });
      toast.success('ทำรายการสำเร็จ');
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCrypto) return handleCryptoSubmit();

    let finalAction: 'CASH_DEPOSIT' | 'CASH_WITHDRAW' | 'CASH_ADJUST' = 'CASH_DEPOSIT';
    let finalAmount = 0;
    let finalNote = '';

    if (displayMode === 'V1') {
      // 🟢 Logic V1: คำนวณส่วนต่างระหว่าง 'ยอดที่มีอยู่' (currentCash) กับ 'ยอดที่ต้องการ' (amount)
      // ถ้ามี 1000 แล้วอยากให้เหลือ 100 ต้องส่ง -900 ไป
      finalAmount = Number(amount) - currentCash;
      finalAction = 'CASH_ADJUST';
    } else {
      if (isAdjustMode) {
        // 🟢 โหมด "ปรับสมดุล" (CASH_ADJUST)
        if (targetBalance === '' || targetBalance < 0) return toast.error('กรุณาระบุยอดเงินเป้าหมายที่ถูกต้อง');
        if (difference === 0) return toast.error('ยอดเงินเท่าเดิม ไม่มีการเปลี่ยนแปลง');

        finalAction = 'CASH_ADJUST';
        // ✅ เปลี่ยนจาก Number(targetBalance) เป็น difference (ซึ่งคำนวณไว้แล้วว่าคือ -2 หรือ +X)
        finalAmount = difference;
        finalNote = 'ปรับข้อมูล'; // บังคับ Note

      } else {
        // 🔵 โหมด "ปกติ" (DEPOSIT / WITHDRAWAL)
        if (!amount || amount <= 0) return toast.error('กรุณาระบุจำนวนเงินที่ถูกต้อง');
        if (actionType === 'CASH_WITHDRAW' && amount > currentCash) return toast.error('ยอดเงินสดไม่เพียงพอสำหรับการถอน');

        finalAction = actionType;
        finalAmount = Number(amount);
        finalNote = note;
      }
    }

    setIsLoading(true);
    try {
      // 🚀 ยิง API
      if (displayMode === 'V1') {

        await manageCashV1(portfolioId, { amount: finalAmount, cashCurrency: 'USD', expectedRatio: null, type: finalAction });
      } else {
        await manageV2CashTransaction(portfolioId, finalAction, finalAmount, finalNote, transactionDate);
      }

      toast.success('ทำรายการสำเร็จ');
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsLoading(false);
    }
  };
 

  // 2. ใช้ useEffect เพื่อ sync ค่าจาก props/context กลับมาที่ displayAmount
  useEffect(() => {
    setDisplayAmount(amount.toString());
  }, [amount]);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-sm bg-[#18181b] border border-gray-700 rounded-2xl shadow-2xl animate-fadeIn">

        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-green-400">
            {isAdjustMode ? 'ปรับข้อมูลยอดเงินสด' : 'จัดการเงินสด (Cash)'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl font-bold">&times;</button>
        </div>



        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* แสดงยอดเงินปัจจุบัน */}
          <div className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg flex justify-between items-center">
            <span className="text-sm text-gray-400">ยอดเงินสดปัจจุบัน:</span>
            {displayMode === 'V1' ? (
              <div className="flex items-center justify-center w-full px-4">
                <span className="text-3xl font-black text-gray-500 mr-2">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={displayAmount}
                  onChange={(e) => {
                    const val = e.target.value;

                    // อนุญาตให้พิมพ์ตัวเลขและจุดทศนิยมได้เท่านั้น
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setDisplayAmount(val);

                      // อัปเดตค่าจริงเข้าสู่ state หลัก (เปลี่ยนเป็น number)
                      setAmount(val === '' ? 0 : parseFloat(val));
                    }
                  }}
                  // เมื่อออกจากช่อง input ให้ทำการ format เลขให้สวยงาม
                  onBlur={() => {
                    setDisplayAmount(amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                  }}
                  // เมื่อคลิกโฟกัส ให้เอา comma ออกเพื่อให้พิมพ์ต่อได้ง่าย
                  onFocus={() => {
                    setDisplayAmount(amount.toString());
                  }}
                  className="w-full max-w-[180px] text-center text-2xl font-black text-yellow-400 bg-transparent border-b-2 border-transparent focus:border-yellow-500 transition-colors focus:outline-none"
                  placeholder="0.00"
                />
              </div>
            ) : (
             <span className={`text-xl font-black transition-colors ${isModified ? 'text-green-300' : 'text-white'}`}>
                ${projectedCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>

          {displayMode !== 'V1' && !isAdjustMode ? (
            // ------------------ โหมดฝาก/ถอน ปกติ ------------------
            <div className="flex flex-col gap-4 animate-fadeIn">
              <div className="flex bg-gray-900 rounded-lg p-1 border border-gray-700">
                <button
                  type="button"
                  onClick={() => setActionType('CASH_DEPOSIT')}
                  className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${actionType === 'CASH_DEPOSIT' ? 'bg-green-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  ฝากเงิน
                </button>
                <button
                  type="button"
                  onClick={() => setActionType('CASH_WITHDRAW')}
                  className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${actionType === 'CASH_WITHDRAW' ? 'bg-red-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  ถอนเงิน
                </button>
              </div>
              <div className="">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  วันที่ทำรายการ<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={transactionDate}
                  max={new Date().toISOString().split('T')[0]} // ป้องกันเลือกวันในอนาคต
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">จำนวนเงิน (USD) <span className="text-red-500">*</span></label>
                {isCrypto ? (
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={amountText}
                    onChange={(e) => {
                      const v = sanitizeDecimalInput(e.target.value);
                      setAmountText(v);
                      setAmount(v ? num(v) : '');
                    }}
                    className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-400 text-lg font-bold"
                    placeholder="0.00"
                    required={!isAdjustMode}
                  />
                ) : (
                <input
                  type="number" min="0.01" step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-400 text-lg font-bold"
                  placeholder="0.00"
                  required={!isAdjustMode}
                />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">บันทึกช่วยจำ (Note)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-4 py-2 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-400 text-sm"
                  placeholder="เช่น เติมเงินประจำเดือน..."
                />
              </div>

              {/* ปุ่มเปิดโหมด Adjust */}
              <div className="flex justify-end mt-[-5px]">
                <button
                  type="button"
                  onClick={() => setIsAdjustMode(true)}
                  className="text-[12px] text-gray-400 hover:text-yellow-400 underline transition-colors"
                >
                  ยอดเงินไม่ตรง? ปรับลดยอดเงินสดที่นี่
                </button>
              </div>
            </div>
          ) : (
            // ------------------ โหมดปรับยอดเงิน (Adjust Mode) ------------------
            <div className="flex flex-col gap-4 animate-fadeIn">

              {displayMode === 'V2' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">วันที่ทำรายการ</label>
                    <input
                      type="date"
                      value={transactionDate}
                      max={new Date().toISOString().split('T')[0]} // ป้องกันเลือกวันในอนาคต
                      onChange={(e) => setTransactionDate(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">ระบุยอดเงินสดที่ถูกต้อง (USD) <span className="text-red-500">*</span></label>

                    {isCrypto ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={targetText}
                        onChange={(e) => {
                          const v = sanitizeDecimalInput(e.target.value);
                          setTargetText(v);
                          setTargetBalance(v ? num(v) : '');
                        }}
                        className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 text-lg font-bold"
                        placeholder="ระบุยอดคงเหลือล่าสุด"
                        required={isAdjustMode}
                      />
                    ) : (
                    <input
                      type="number" min="0" step="0.01"
                      value={targetBalance}
                      onChange={(e) => setTargetBalance(Number(e.target.value))}
                      className="w-full px-4 py-2.5 bg-[#232428] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400 text-lg font-bold"
                      placeholder="ระบุยอดคงเหลือล่าสุด"
                      required={isAdjustMode}
                    />
                    )}
                  </div>

                  {/* พรีวิวว่าระบบจะทำอะไรให้ */}
                  {targetBalance !== '' && difference !== 0 && (
                    <div className={`p-3 rounded-lg text-sm border ${difference > 0 ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'}`}>
                      <span>💡 ระบบจะสร้างรายการ <b>{difference > 0 ? 'ฝากเงิน' : 'ถอนเงิน'}</b> อัตโนมัติ จำนวน <b>${Math.abs(difference).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b> และใส่หมายเหตุว่า "ปรับข้อมูล"</span>
                    </div>
                  )}

                  {/* ปุ่มกลับไปโหมดปกติ */}
                  <div className="flex justify-end mt-[-5px]">
                    <button
                      type="button"
                      onClick={() => setIsAdjustMode(false)}
                      className="text-[12px] text-gray-400 hover:text-white underline transition-colors"
                    >
                      &larr; กลับไปหน้าฝาก/ถอนปกติ
                    </button>

                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} disabled={isLoading} className="px-5 py-2.5 rounded-lg font-bold text-gray-300 hover:bg-gray-700 transition-colors">ยกเลิก</button>
            <button
              type="submit"
              disabled={isLoading || (isAdjustMode && difference === 0)}
              className={`px-5 py-2.5 rounded-lg font-bold text-white transition-all active:scale-95 disabled:opacity-50 ${isAdjustMode ? 'bg-yellow-500 hover:bg-yellow-400 text-black' : (actionType === 'CASH_DEPOSIT' ? 'bg-green-500 hover:bg-green-400' : 'bg-red-500 hover:bg-red-400')}`}
            >
              {isLoading ? 'กำลังดำเนินการ...' : 'ยืนยันทำรายการ'}
            </button>
          </div>
        </form>


      </div>
    </div>
  );
};

export default ManageCashModal;